import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { Api } from './api/client';

type Listener = (type: string, data: unknown) => void;

export interface CallEventBus {
  subscribe(listener: Listener): () => void;
}

const BusContext = createContext<CallEventBus | null>(null);

/** One SSE connection per signed-in session, shared by the banner and every page. */
export function CallEventsProvider({ api, children }: { api: Api; children: ReactNode }) {
  const listeners = useRef(new Set<Listener>());
  const bus = useMemo<CallEventBus>(
    () => ({
      subscribe(l) {
        listeners.current.add(l);
        return () => listeners.current.delete(l);
      },
    }),
    [],
  );

  useEffect(() => {
    let stop: (() => void) | undefined;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const connect = () => {
      api
        .events((type, data) => listeners.current.forEach((l) => l(type, data)))
        .then((s) => {
          if (cancelled) s();
          else stop = s;
        })
        .catch(() => {
          if (!cancelled) retry = setTimeout(connect, 5000);
        });
    };
    connect();
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      stop?.();
    };
  }, [api]);

  return <BusContext.Provider value={bus}>{children}</BusContext.Provider>;
}

export function useCallEvents(listener: Listener) {
  const bus = useContext(BusContext);
  useEffect(() => bus?.subscribe(listener), [bus, listener]);
}

/** Per-browser preference with a safe fallback when storage is unavailable. */
export function getPref(key: string, fallback = false): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === 'true';
  } catch {
    return fallback;
  }
}
export function setPref(key: string, value: boolean) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* storage unavailable */
  }
}

/** A per-viewer list preference (e.g. which lines auto-open). Empty/unset -> []. */
export function getPrefList(key: string): string[] {
  try {
    const v = localStorage.getItem(key);
    const parsed = v ? (JSON.parse(v) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
export function setPrefList(key: string, value: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}
